/**
 * 업무 프로젝트(PMS) J3c2b — 업무 상세 서랍의 «첨부파일» 구역 안쪽.
 *
 * 목록(최신 순)·올리기(버튼·끌어 놓기)·내려받기·지우기를 맡는다. 바깥 <section> 은 상세가 그린다.
 * - 🔴 상한·허용 확장자는 서버 값의 '거울' 이다(`backend/services/task_pm_files.py:24-25 · 33-53`).
 *   서버를 바꾸면 여기도 같이 바꾼다. 잔디는 30MB·10개지만 우리 서버는 20MB·20개다.
 * - 🔴 20MB 초과는 화면에서 먼저 막는다 — nginx `/api/` 의 client_max_body_size 가 25M 라
 *   그보다 큰 파일은 JSON 없는 413 으로 끊겨 서버 문구가 사용자에게 닿지 않는다.
 * - 🔴 파일 목록(FileList)은 이벤트가 끝나면 비워질 수 있다(drop 의 dataTransfer · 입력칸 value 초기화)
 *   → 핸들러 안에서 첫 await 전에 배열로 복사해 넘긴다.
 * - 🔴 '올리는 중' 판정은 state 가 아니라 busyRef 로 한다 — state 는 그 렌더 시점 값이라
 *   연속 선택이 낡은 false 를 보고 같은 요청을 겹쳐 보낸다.
 * - 🔴 목록이 도착하기 전에는 올리지 않는다 — 나중에 도착한 목록 응답이 방금 넣은 항목을 덮는다.
 * - 🔴 내려받기 오류 본문은 blob 이라 errorText 가 못 읽는다 → 글자로 읽어 detail 을 꺼낸다
 *   (그래야 410 «저장된 파일을 찾을 수 없습니다.» 가 보인다). JSON 이 아니면 본문을 그대로
 *   보이지 않고 기본 문구를 쓴다(nginx HTML 이 그대로 뜨지 않게).
 * - 실패 문구는 지어내지 않고 서버 detail 을 그대로 보인다(errorText 로 문자열화).
 */
import { useEffect, useRef, useState } from 'react';
import { taskPmAPI } from '../../api/client';
import { alertDialog, confirmDialog, toast } from '../../components/AppDialog';
import { errorText } from './errorText';
import { COLORS, RADIUS, btn } from '../../utils/uiStyles';

const MAX_FILE_MB = 20;                                  // = task_pm_files.MAX_FILE_BYTES
const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024;
const MAX_FILES = 20;                                    // = task_pm_files.MAX_FILES_PER_TASK
// = task_pm_files.ALLOWED_EXTS (실행 파일·HTML 은 서버가 받지 않는다)
const ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.xlsm,.csv,.ppt,.pptx,.hwp,.hwpx,.txt,.md,.png,.jpg,.jpeg,.gif,.webp,.zip';

const TOO_LARGE = `첨부 파일은 ${MAX_FILE_MB}MB를 초과할 수 없습니다.`;   // = task_pm_files.FILE_TOO_LARGE
const LOAD_FAIL = '첨부 목록을 불러오지 못했습니다.';
const UPLOAD_FAIL = '파일을 올리지 못했습니다.';
const DOWNLOAD_FAIL = '파일을 내려받지 못했습니다.';
const DELETE_FAIL = '파일을 지우지 못했습니다.';
const UPLOAD_BUSY = '파일을 올리는 중입니다. 끝난 뒤 다시 시도해 주세요.';

// jsdom 16 에는 Blob.text() 가 없다 → FileReader 갈래로 읽는다(선례 `AbcAnalysisPage.jsx`).
const readBlobText = (blob) => (typeof blob.text === 'function'
  ? blob.text()
  : new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  }));

const formatBytes = (value) => {
  const n = Number(value) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

// 상세의 구역 제목과 같은 값(잔디 섹션 제목 16px).
const sectionTitle = { margin: 0, fontSize: '16px' };
const headRow = { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' };
const subText = { fontSize: '11px', color: COLORS.textSub, whiteSpace: 'nowrap' };
const mutedLine = { margin: 0, fontSize: '12px', color: COLORS.textSub };
const fileRow = {
  display: 'flex', alignItems: 'center', gap: '8px',
  padding: '6px 0', borderBottom: `1px solid ${COLORS.borderSoft}`,
};
const nameBtn = {
  background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'inherit',
  color: COLORS.primaryDark, fontSize: '13px', textAlign: 'left', flex: 1, minWidth: 0, wordBreak: 'break-all',
};

export default function TaskPmAttachments({ taskId, project, canWrite, onChanged }) {
  // 🔴 렌더마다 읽는다(모듈 최상위 상수 금지 — 로그인 전 빈 값으로 굳는다. 선례 TaskPmComments).
  const me = (() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
  })();
  const myId = me?.id;

  const [items, setItems] = useState(null);      // null = 불러오는 중
  const [loadError, setLoadError] = useState('');
  const [busy, setBusy] = useState(false);       // 버튼 표시용(판정은 busyRef)
  const [dragOver, setDragOver] = useState(false);

  const busyRef = useRef(false);
  const aliveRef = useRef(true);
  const inputRef = useRef(null);

  // 언마운트 뒤에는 상태를 건드리지 않고, 남은 파일도 올리지 않는다.
  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    setItems(null);
    setLoadError('');
    taskPmAPI.listAttachments(taskId)
      .then((res) => {
        if (!alive) return;
        setItems(Array.isArray(res?.data?.items) ? res.data.items : []);
      })
      .catch((e) => {
        if (!alive) return;
        setLoadError(errorText(e, LOAD_FAIL));
      });
    return () => { alive = false; };
  }, [taskId]);

  // 상세 서랍의 reportError 와 같은 모양(상태 코드가 있으면 대화상자, 없으면 알림).
  const reportError = (e, fallback) => {
    const message = errorText(e, fallback);
    if (e?.response?.status) alertDialog(message); else toast(message, 'error');
  };

  // 🔴 인자는 '이미 배열' 이다 — 호출하는 핸들러가 첫 await 전에 복사해 넘긴다.
  const uploadFiles = async (list) => {
    if (!canWrite || items === null || !list.length) return;
    if (busyRef.current) { toast(UPLOAD_BUSY, 'info'); return; }

    // 🔴 크기를 먼저 거른다 — 알림은 await 하지 않는다(await 하면 busy 해제가 늦어져
    //    연속 선택이 막힌다). 큰 파일이 남은 자리를 먹지 않게 자르기 전에 거른다.
    const sized = [];
    list.forEach((file) => {
      if (file.size > MAX_FILE_BYTES) alertDialog(`${file.name}: ${TOO_LARGE}`);
      else sized.push(file);
    });
    if (!sized.length) return;

    const room = MAX_FILES - items.length;
    if (room <= 0) { toast(`최대 ${MAX_FILES}개까지 올릴 수 있습니다.`, 'info'); return; }
    let queue = sized;
    if (sized.length > room) {
      queue = sized.slice(0, room);
      toast(`최대 ${MAX_FILES}개까지 올릴 수 있어 ${sized.length - room}개는 올리지 않았습니다.`, 'info');
    }

    busyRef.current = true;
    setBusy(true);
    let added = 0;
    try {
      for (const file of queue) {
        if (!aliveRef.current) break;
        try {
          const res = await taskPmAPI.uploadAttachment(taskId, file);
          if (!aliveRef.current) break;
          if (res?.data) setItems((prev) => [res.data, ...(prev || [])]);   // 목록은 최신 순
          added += 1;
        } catch (e) {
          // 🔴 남은 파일은 멈춘다 — 개수 초과·권한 실패가 줄줄이 뜨지 않게.
          if (aliveRef.current) reportError(e, UPLOAD_FAIL);
          break;
        }
      }
    } finally {
      busyRef.current = false;
      if (aliveRef.current) setBusy(false);
    }
    if (added && aliveRef.current) onChanged?.();    // «기록» 탭·업무 표를 다시 읽게 한다
  };

  const pickFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';            // 같은 파일을 다시 고를 수 있게(복사 뒤에 비운다)
    uploadFiles(picked);
  };

  const download = async (item) => {
    try {
      const res = await taskPmAPI.downloadAttachment(item.id);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = item.name || 'attachment';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      const data = e?.response?.data;
      let detail = null;
      if (data instanceof Blob) {
        try {
          const text = await readBlobText(data);
          try {
            const parsed = JSON.parse(text);
            if (typeof parsed?.detail === 'string') detail = parsed.detail;
          } catch { /* JSON 아님(nginx HTML 등) — 본문을 그대로 보이지 않는다 */ }
        } catch { /* 읽기 실패 — 기본 문구를 쓴다 */ }
      }
      if (!aliveRef.current) return;
      const message = detail || errorText(e, DOWNLOAD_FAIL);
      if (e?.response?.status) alertDialog(message); else toast(message, 'error');
    }
  };

  const remove = async (item) => {
    if (!(await confirmDialog(`${item.name} 파일을 지울까요?`))) return;
    try {
      await taskPmAPI.deleteAttachment(item.id);      // 204 — 그 줄만 빼낸다
      if (!aliveRef.current) return;
      setItems((prev) => (prev || []).filter((x) => x.id !== item.id));
      onChanged?.();
    } catch (e) {
      if (aliveRef.current) reportError(e, DELETE_FAIL);
    }
  };

  // 서버 `task_pm_attachments.py:225-226` 과 같은 판단.
  const canDelete = (item) => Boolean(canWrite) && (item.uploader_id === myId || project?.my_role === 'manager');

  const count = items ? items.length : 0;
  const pickDisabled = !canWrite || busy || items === null || count >= MAX_FILES;
  const edge = dragOver ? COLORS.primary : COLORS.inputBorder;

  const emptyBox = {
    border: `1px dashed ${edge}`, borderRadius: RADIUS.md, padding: '16px',
    textAlign: 'center', fontSize: '12px', color: COLORS.textSub,
  };

  const body = () => {
    if (loadError) {
      return <p role="alert" style={{ margin: 0, fontSize: '12px', color: COLORS.dangerText }}>{loadError}</p>;
    }
    if (items === null) return <p role="status" style={mutedLine}>불러오는 중…</p>;
    if (items.length === 0) {
      return (
        <div style={emptyBox}>
          <p style={{ margin: 0 }}>첨부된 파일이 없습니다.</p>
          {/* 🔴 안내 문구는 줄을 나눈다 — 한 덩어리로 붙이면 «파일당 최대 20MB, 최대 20개» 를 찾을 수 없다. */}
          <p style={{ margin: '2px 0 0' }}>{`파일당 최대 ${MAX_FILE_MB}MB, 최대 ${MAX_FILES}개`}</p>
          {canWrite && <p style={{ margin: '2px 0 0' }}>여기에 끌어 놓아도 됩니다.</p>}
        </div>
      );
    }
    return (
      <ul aria-label="첨부 목록"
        style={{ listStyle: 'none', margin: 0, padding: 0, border: dragOver ? `1px dashed ${edge}` : 'none' }}>
        {items.map((item) => (
          <li key={item.id} style={fileRow}>
            {/* 🔴 행마다 되풀이되는 버튼이라 이름은 aria-label 로 구별한다. */}
            <button type="button" aria-label={`${item.name} 내려받기`} onClick={() => download(item)} style={nameBtn}>
              {item.name}
            </button>
            <span style={subText}>{formatBytes(item.bytes)}</span>
            <span style={subText}>{item.uploader_name || '알 수 없음'}</span>
            {canDelete(item) && (
              <button type="button" aria-label={`${item.name} 삭제`} onClick={() => remove(item)} style={btn('secondary', 'sm')}>삭제</button>
            )}
          </li>
        ))}
      </ul>
    );
  };

  const onDragOver = (e) => {
    if (!canWrite) return;
    e.preventDefault();          // 막지 않으면 브라우저가 파일을 그냥 열어 버린다
    setDragOver(true);
  };

  // 자식 위를 지날 때 깜빡이지 않게 — 구역 밖으로 나갔을 때만 끈다.
  const onDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (!canWrite) return;
    // 🔴 dataTransfer 는 이벤트가 끝나면 비므로 지금 배열로 복사한다.
    uploadFiles(Array.from(e.dataTransfer?.files || []));
  };

  return (
    <div data-testid="task-attachments-drop" onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}>
      <div style={headRow}>
        {/* 🔴 제목은 템플릿 문자열 한 덩어리로 둔다(숫자를 span 으로 쪼개면 시험·읽기가 깨진다). */}
        <h4 style={sectionTitle}>{`첨부파일 (${count}/${MAX_FILES})`}</h4>
        <button type="button" onClick={() => inputRef.current?.click()} disabled={pickDisabled}
          style={{ ...btn('secondary', 'sm'), marginLeft: 'auto', opacity: pickDisabled ? 0.5 : 1 }}>파일 첨부</button>
        <input type="file" multiple accept={ACCEPT} aria-label="첨부할 파일" hidden ref={inputRef} onChange={pickFiles} />
      </div>
      {body()}
    </div>
  );
}
